import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormStepProps } from '../../types/application';

const StepContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 10px;
  
  @media (min-width: 768px) {
    padding: 0;
  }
`;

const StepTitle = styled.h2`
  color: ${props => props.theme.primaryColor};
  font-size: 1.4rem;
  margin-bottom: 8px;
  font-weight: 600;
  
  @media (min-width: 768px) {
    font-size: 1.8rem;
  }
`;

const StepDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 20px;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 30px;
  }
`;

const InfoBox = styled.div`
  background: #e3f2fd;
  border: 1px solid ${props => props.theme.primaryColor};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  
  h3 {
    color: ${props => props.theme.primaryColor};
    margin: 0 0 10px 0;
    font-size: 1.1rem;
  }
  
  p {
    margin: 0;
    color: #555;
    line-height: 1.5;
  }
`;

const ChecklistContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  
  @media (min-width: 768px) {
    padding: 20px;
  }
`;

const ChecklistTitle = styled.h3`
  color: ${props => props.theme.primaryColor};
  margin: 0 0 20px 0;
  font-size: 1.2rem;
`;

const ChecklistItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 15px;
  padding: 15px;
  background: white;
  border-radius: 6px;
  border: 2px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.primaryColor};
    background-color: #f8f9fa;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Checkbox = styled.input`
  margin-top: 2px;
  transform: scale(1.2);
  accent-color: ${props => props.theme.primaryColor};
  cursor: pointer;
`;

const ItemContent = styled.div`
  flex: 1;
  cursor: pointer;
  
  .item-title {
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
    line-height: 1.3;
  }
  
  .item-description {
    color: #666;
    font-size: 13px;
    line-height: 1.4;
  }
  
  .item-requirements {
    color: #d32f2f;
    font-size: 12px;
    margin-top: 4px;
    font-style: italic;
  }
`;

const RequirementBadge = styled.span<{ type: 'required' | 'conditional' }>`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 600;
  margin-left: 8px;
  
  ${props => props.type === 'required' ? `
    background: #ffebee;
    color: #d32f2f;
  ` : `
    background: #fff3e0;
    color: #f57c00;
  `}
`;

const ProcessInfo = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: linear-gradient(135deg, ${props => props.theme.primaryColor} 0%, ${props => props.theme.primaryColor}dd 100%);
  color: white;
  border-radius: 8px;
  
  @media (min-width: 768px) {
    margin-top: 30px;
    padding: 20px;
  }
  
  h4 {
    margin: 0 0 15px 0;
    font-size: 1rem;
    
    @media (min-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .process-steps {
    display: grid;
    gap: 8px;
    
    @media (min-width: 768px) {
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
  }
  
  .step {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    
    @media (min-width: 768px) {
      font-size: 14px;
    }
    
    .step-number {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      width: 22px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 600;
      flex-shrink: 0;
      
      @media (min-width: 768px) {
        width: 24px;
        height: 24px;
        font-size: 12px;
      }
    }
  }
`;

const documentRequirements = [
  {
    id: 'cert_incorporation',
    title: 'Company Certificate of Incorporation',
    description: 'Official business registration document or equivalent',
    required: true,
    requirements: 'Must be from official government registry'
  },
  {
    id: 'director_id',
    title: 'Director\'s Current Official Photo ID',
    description: 'Passport or government-issued ID in full colour',
    required: true,
    requirements: 'High quality photos required, scanned copies not accepted'
  },
  {
    id: 'director_address_proof',
    title: 'Director\'s Address Proof',
    description: 'Utility bill, bank statement, or government correspondence',
    required: true,
    requirements: 'Must be within last 3 months, full colour'
  },
  {
    id: 'owners_id',
    title: 'All Major Owners\' Photo IDs',
    description: 'Current official photo IDs for all owners with >25% shareholding',
    required: false,
    conditional: 'If multiple major owners',
    requirements: 'Full colour, government-issued IDs only'
  },
  {
    id: 'owners_address_proof',
    title: 'All Major Owners\' Address Proof',
    description: 'Address verification for all major owners',
    required: false,
    conditional: 'If multiple major owners',
    requirements: 'Within last 3 months, full colour'
  },
  {
    id: 'company_utilities',
    title: 'Company Utilities or Office Rental',
    description: 'Documentation showing business address',
    required: true,
    requirements: 'Within last 3 months'
  },
  {
    id: 'business_photos',
    title: 'Business Premises Photos',
    description: '1 interior and 1 exterior photo showing address/signage',
    required: true,
    requirements: 'High quality colour photos, minimum 2 photos'
  },
  {
    id: 'bank_statements',
    title: 'Company Bank Statements',
    description: 'Statements showing company name, account details and transactions',
    required: true,
    requirements: 'Within last 3 months, from major financial institution'
  }
];

const DocumentUpload: React.FC<FormStepProps & { onValidationChange: (isValid: boolean) => void }> = ({
  data,
  updateData,
  onValidationChange
}) => {
  const [acknowledgedDocuments, setAcknowledgedDocuments] = useState<string[]>([]);

  const handleDocumentAcknowledge = (documentId: string, checked: boolean) => {
    if (checked) {
      setAcknowledgedDocuments(prev => [...prev, documentId]);
    } else {
      setAcknowledgedDocuments(prev => prev.filter(id => id !== documentId));
    }
  };

  useEffect(() => {
    const requiredDocs = documentRequirements.filter(doc => doc.required);
    const allRequiredAcknowledged = requiredDocs.every(doc => 
      acknowledgedDocuments.includes(doc.id)
    );
    onValidationChange(allRequiredAcknowledged);
  }, [acknowledgedDocuments, onValidationChange]);

  return (
    <StepContainer>
      <StepTitle>Document Requirements</StepTitle>
      <StepDescription>
        Please review the document requirements below. Once our application team approves your information, 
        we will provide secure document verification links for uploading your KYC documents.
      </StepDescription>

      <InfoBox>
        <h3>📋 Important Information</h3>
        <p>
          This is a preview of document requirements. Actual document upload will be enabled after initial application approval. 
          Requirements may vary by jurisdiction and business type.
        </p>
      </InfoBox>

      <ChecklistContainer>
        <ChecklistTitle>Document Checklist</ChecklistTitle>
        
        {documentRequirements.map(doc => (
          <ChecklistItem key={doc.id}>
            <Checkbox
              type="checkbox"
              checked={acknowledgedDocuments.includes(doc.id)}
              onChange={(e) => handleDocumentAcknowledge(doc.id, e.target.checked)}
            />
            <ItemContent>
              <div className="item-title">
                {doc.title}
                <RequirementBadge type={doc.required ? 'required' : 'conditional'}>
                  {doc.required ? 'REQUIRED' : 'CONDITIONAL'}
                </RequirementBadge>
              </div>
              <div className="item-description">
                {doc.description}
                {doc.conditional && ` (${doc.conditional})`}
              </div>
              <div className="item-requirements">
                {doc.requirements}
              </div>
            </ItemContent>
          </ChecklistItem>
        ))}
      </ChecklistContainer>

      <ProcessInfo>
        <h4>📄 Document Verification Process</h4>
        <div className="process-steps">
          <div className="step">
            <div className="step-number">1</div>
            Initial application approval
          </div>
          <div className="step">
            <div className="step-number">2</div>
            Secure upload links provided
          </div>
          <div className="step">
            <div className="step-number">3</div>
            Global ID verification with Onfido/Jumio
          </div>
          <div className="step">
            <div className="step-number">4</div>
            Document authentication & compliance review
          </div>
          <div className="step">
            <div className="step-number">5</div>
            Jurisdiction-specific compliance checks
          </div>
          <div className="step">
            <div className="step-number">6</div>
            Final approval & account setup
          </div>
        </div>
      </ProcessInfo>
    </StepContainer>
  );
};

export default DocumentUpload;